import { ScheduledEventDto, UserDto } from '@act/shared/data-transfer-objects';
import { FlexContainer } from '@act/storybook';
import { useMemo } from 'react';
import { Typography } from '@mui/material';
import { TrackingProvider } from 'react-components/tracking';
import CompositeDialog from '../dialog/CompositeDialog';
import { DialogTitleWithRecoveredNote } from './components/DialogTitleWithRecoveredNote';
import { ScheduledEventForm } from './ScheduledEventForm';
import { useScheduledEventForm } from './hooks/useScheduledEventForm.hook';

export interface ScheduledEventDialogInterfaceProps {
  user: UserDto;
  scheduledEvent?: ScheduledEventDto;
}

interface ScheduledEventDialogProps extends ScheduledEventDialogInterfaceProps {
  handleClose: () => void;
  mode: 'Create' | 'Edit' | 'Next';
  open: boolean;
}

/**
 * Create/edit scheduled event dialog for manual scheduling flows.
 * @param props - Member, optional existing event, mode, and close handler.
 * @returns The scheduled-event dialog.
 */
const ScheduledEventDialog = ({
  handleClose,
  mode,
  user,
  scheduledEvent,
  open,
}: ScheduledEventDialogProps) => {
  const {
    control,
    formState,
    handleSubmit,
    handleSave,
    handleClose: handleFormClose,
    formConfig,
    isSaving,
    showRecoveredNote,
    isEditMode,
  } = useScheduledEventForm({
    user,
    scheduledEvent,
    open,
    onClose: () => handleClose(),
    includeTrackingNames: true,
  });

  const scheduledEventDialogTitle = `${
    mode === 'Next' ? 'Schedule next event' : 'Schedule event'
  } for ${user.firstName} ${user.lastName}`;

  const dialogTitle = useMemo(
    () => (
      <DialogTitleWithRecoveredNote
        title={scheduledEventDialogTitle}
        showRecoveredNote={showRecoveredNote}
      />
    ),
    [scheduledEventDialogTitle, showRecoveredNote]
  );

  return (
    <TrackingProvider contextName="scheduled-event-dialog">
      <CompositeDialog
        handleClose={handleFormClose}
        maxWidth="md"
        open={open}
        title={dialogTitle}
        handleFormSubmit={handleSubmit(data => {
          void handleSave(data);
        })}
        actions={[
          {
            text: 'Cancel',
            callback: () => handleFormClose(),
            variant: 'outlined',
            disabled: isSaving,
          },
          {
            text: isSaving
              ? 'Saving...'
              : isEditMode
              ? 'Save'
              : 'Schedule event',
            type: 'submit',
            variant: 'contained',
            isPrimary: true,
            disabled: isSaving,
          },
        ]}
      >
        {mode === 'Next' && (
          <FlexContainer direction="column" padding="16px 0">
            <Typography variant="bodyLarge">
              Please schedule your next interaction with this member.
            </Typography>
          </FlexContainer>
        )}
        <ScheduledEventForm
          control={control}
          formState={formState}
          formConfig={formConfig}
        />
      </CompositeDialog>
    </TrackingProvider>
  );
};

export default ScheduledEventDialog;
